import { useCallback, useEffect, useState } from 'react';

import { AllieColors } from '../../../../common/color.types';
import {
  AllieToolbarMobileAppModalStyled,
  LogoutMobileAppModalContent,
  LogoutMobileAppModalHeader,
  LogoutMobileAppModalStyled,
  StyledRoundButton,
} from './LogoutModal.mobileApp.styles';
import { AllieArrowLeftIcon, AllieChevronBackOutline, usePlatformDetection } from '../../../..';
import { AllieButton, AllieText } from '../../../../components';
import { isPlatform } from '@ionic/react';

export interface LogoutMobileAppModalProps {
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
  children: React.ReactNode;
  title?: string;
  footer?: React.ReactNode;
  zIndex?: number;
  overflowY?: 'auto' | 'hidden';
  toolbarEnd?: React.ReactNode;
  backgroundColor?: AllieColors;
}

export interface LogoutMobileAppModalStylesProps {
  isOpen?: boolean;
  overflowY?: 'auto' | 'hidden';
  backgroundColor?: AllieColors;
}

export interface LogoutMobileAppModalContentProps {
  hide?: boolean;
  overflowY?: 'auto' | 'hidden';
  isWidget?: boolean;
}

export const LogoutMobileAppModal: React.FC<LogoutMobileAppModalProps> = ({
  isOpen,
  setIsOpen,
  children,
  title,
  footer,
  zIndex,
  overflowY = 'auto',
  toolbarEnd,
  backgroundColor,
}) => {
  const [hide, setHide] = useState(true);
  const { platforms } = usePlatformDetection();
  const isIos = platforms.includes('ios');
  const handleBackButton = useCallback((event: PopStateEvent) => {
    event.preventDefault();
    setIsOpen(false);
  }, []);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (!isOpen) {
      timeout = setTimeout(() => {
        setHide(true);
      }, 500);
    } else {
      setHide(false);
    }
    return () => clearTimeout(timeout);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      window.history.pushState(null, '', window.location.pathname);
      window.addEventListener('popstate', handleBackButton);
    } else {
      window.removeEventListener('popstate', handleBackButton);
    }

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [isOpen, handleBackButton]);

  return (
    <LogoutMobileAppModalStyled
      isOpen={isOpen}
      zIndex={zIndex}
      backgroundColor={backgroundColor}
      style={{
        borderRadius: 0,
        ...(isIos && { height: 'calc(100vh - 65px)' }),
      }}
    >
      <LogoutMobileAppModalHeader isAndroid={isPlatform('android')}>
        <AllieToolbarMobileAppModalStyled>
          <div slot="start">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--allie-spacing-xl)',
                padding: 'var(--allie-spacing-xl) var(--allie-spacing-3xl)',
              }}
            >
              <StyledRoundButton
                allieSize="none"
                allieType="subtle"
                corner="rounded"
                onclick={() => setIsOpen(false)}
              >
                <AllieArrowLeftIcon display="flex" style={{ width: '20px', height: '20px' }} />
              </StyledRoundButton>
              <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
                <AllieText type="h3" color="neutral-90" weight={600}>
                  {title}
                </AllieText>
              </div>
            </div>
          </div>
          <div slot="end">{toolbarEnd}</div>
        </AllieToolbarMobileAppModalStyled>
      </LogoutMobileAppModalHeader>
      <LogoutMobileAppModalContent hide={hide} overflowY={overflowY}>
        {children}
      </LogoutMobileAppModalContent>
      <div>{footer}</div>
    </LogoutMobileAppModalStyled>
  );
};
